import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
  Dimensions,
} from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { PhoneInputComponent } from './design-system/PhoneInput';
import { Input } from './design-system/Input';
import { Button } from './design-system/Button';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { useThemedStyles } from '../hooks/useThemedStyles';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const isMobile = screenWidth < 768;

interface WhatsAppMessageModalProps {
  visible: boolean;
  onClose: () => void;
  onMessageSent?: () => void;
}

export const WhatsAppMessageModal: React.FC<WhatsAppMessageModalProps> = ({
  visible,
  onClose,
  onMessageSent,
}) => {
  const styles = useThemedStyles(getStyles);
  const { user, currentBusiness, currentSubscription } = useAppStore();
  const { navigateTo, showNotification } = useAppContext();
  
  const [phoneNumber, setPhoneNumber] = useState('');
  const [reason, setReason] = useState('');
  const [contactName, setContactName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const phoneInputRef = React.useRef<any>(null);

  const handleSendMessage = async () => {
    if (!phoneNumber.trim() || !reason.trim()) {
      Alert.alert('Missing Information', 'Please enter both phone number and reason for contact.');
      return;
    }

    if (!currentBusiness || !currentSubscription) {
      Alert.alert('Configuration Error', 'No business or subscription found.');
      return;
    }

    try {
      setIsLoading(true);
      
      // Get formatted phone number
      let formattedPhoneNumber = phoneNumber;
      if (phoneInputRef.current) {
        const phoneData = phoneInputRef.current.getValue();
        if (phoneData?.formattedNumber) {
          formattedPhoneNumber = phoneData.formattedNumber;
        } else if (phoneData?.dialCode && !phoneNumber.startsWith('+')) {
          formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
        }
      }

      // Ensure phone number starts with +
      if (!formattedPhoneNumber.startsWith('+')) {
        if (phoneInputRef.current) {
          const phoneData = phoneInputRef.current.getValue();
          if (phoneData?.dialCode) {
            formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
          } else {
            formattedPhoneNumber = `+${phoneNumber}`;
          }
        } else {
          formattedPhoneNumber = `+${phoneNumber}`;
        }
      }

      // Validate phone number - support different country formats
      if (formattedPhoneNumber.length < 8) {
        Alert.alert('Invalid Phone Number', 'Please enter a valid phone number.');
        return;
      }

      // Get WhatsApp credentials
      const wabuService = currentSubscription?.services?.wabu as any;
      const whatsappCredentials = wabuService?.metadata?.whatsapp_credentials || {};
      
      if (!wabuService?.metadata?.whatsapp_phone?.length) {
        Alert.alert('Configuration Error', 'No WhatsApp phone number configured for this business.');
        return;
      }

      const phone = wabuService.metadata.whatsapp_phone[0];
      const phoneId = phone.whatsapp_phone_number_id || phone.phone_id || phone.id || phone.phone_number_id;

      if (!phoneId) {
        Alert.alert('Configuration Error', 'Invalid WhatsApp phone number configuration.');
        return;
      }

      // Navigate to WhatsApp conversation screen with pre-filled data
      navigateTo('whatsapp-conversation', {
        phoneNumber: formattedPhoneNumber,
        contactName: contactName.trim() || formattedPhoneNumber,
        reason: reason.trim(),
      });

      // Reset form
      setPhoneNumber('');
      setReason('');
      setContactName('');
      
      onClose();
      if (onMessageSent) {
        onMessageSent();
      }
    } catch (error) {
      console.error('Error initiating WhatsApp message:', error);
      Alert.alert('Error', 'Failed to initiate WhatsApp message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPhoneNumber('');
    setReason('');
    setContactName('');
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View style={styles.whatsappIcon}>
                <Ionicons name="logo-whatsapp" size={24} color="white" />
              </View>
              <Text style={styles.title}>WhatsApp Message</Text>
            </View>
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.text.secondary} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            <View style={styles.businessInfo}>
              <Text style={styles.businessName}>{currentBusiness?.name}</Text>
              <Text style={styles.businessSubtext}>Send a WhatsApp message</Text>
            </View>

            <View style={styles.formContainer}>
              <View style={styles.phoneInputSection}>
                <Text style={styles.phoneLabel}>Phone Number *</Text>
                <PhoneInputComponent
                  ref={phoneInputRef}
                  value={phoneNumber}
                  onChangeText={setPhoneNumber}
                  onChange={setPhoneNumber}
                  placeholder="Enter phone number"
                  style={styles.phoneInput}
                />
                {!phoneNumber.trim() && isLoading && (
                  <Text style={styles.errorText}>Phone number is required</Text>
                )}
              </View>

              <Input
                label="Contact Name (Optional)"
                value={contactName}
                onChangeText={setContactName}
                placeholder="Enter contact name"
                autoCapitalize="words"
              />

              <Input
                label="Reason for Contact"
                value={reason}
                onChangeText={setReason}
                placeholder="Enter reason for contacting this customer"
                multiline
                numberOfLines={3}
                required
                error={!reason.trim() && isLoading ? 'Reason is required' : undefined}
              />
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <Button
              variant="outlined"
              onPress={handleClose}
              disabled={isLoading}
              style={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={handleSendMessage}
              disabled={isLoading || !phoneNumber.trim() || !reason.trim()}
              loading={isLoading}
              style={styles.sendButton}
            >
              <Ionicons name="send" size={16} color="white" style={styles.buttonIcon} />
              Start Conversation
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  modalContainer: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    width: isMobile ? '95%' : '85%',
    maxWidth: isMobile ? screenWidth - (spacing.lg * 2) : 600,
    maxHeight: '85%',
    elevation: 8,
    shadowColor: colors.text.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  whatsappIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#25D366',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    flex: 1,
  },
  closeButton: {
    padding: spacing.sm,
    marginLeft: spacing.sm,
  },
  content: {
    padding: spacing.lg,
  },
  businessInfo: {
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.lg,
    borderWidth: 1,
    borderColor: colors.border,
  },
  businessName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  businessSubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.primary,
  },
  formContainer: {
    gap: spacing.lg,
  },
  phoneInputSection: {
    marginBottom: spacing.sm,
  },
  phoneLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  phoneInput: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    marginTop: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: spacing.md,
  },
  cancelButton: {
    flex: 1,
  },
  sendButton: {
    flex: 1,
    backgroundColor: '#25D366',
  },
  buttonIcon: {
    marginRight: spacing.xs,
  },
});
